import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { debounceTime, skip } from 'rxjs/operators';
import { MatTooltipModule } from '@angular/material/tooltip';

// service
import { ContactsService } from '../../../services/contacts/contacts.service';
import { MessageService } from '../../../services/message/message.service';
import { GroupAudienceService } from '../../../services/segments/group-audience.service';
import { IPagination, ITag } from '../../../types/contactModule';
import { PromptCardComponent } from '../../toasts-and-cards/prompt-card/prompt-card.component';
import { PaginationComponent } from '../../inputs/pagination/pagination.component';
import { EditTagModalComponent } from '../../forms/edit-tag-modal/edit-tag-modal.component';
import { EmptyStateComponent } from '../../ui/empty-state/empty-state.component';
import { DataTableComponent } from '../../ui/data-table/data-table.component';
import { HasPermissionDirective } from '../../../directives/has-permission.directive';
import { formatCount, formatWhen } from '../../../../utils/sms';
import { CountState, GroupSelection, countNoun } from '../group-table/group-rows';

interface TagRow {
  tag: ITag;
  key: string;
  name: string;
  description: string | null;
  created: string;
  createdTitle: string | null;
  updated: string;
  updatedTitle: string | null;
}

function stampTitle(value: string | null | undefined): string | null {
  return value ? new Date(value).toLocaleString('en-GB') : null;
}

function toRows(tags: ITag[]): TagRow[] {
  return (tags ?? [])
    .filter((tag) => !!tag && typeof tag === 'object')
    .map((tag, index) => {
      const updated = tag.modifiedAt || tag.createdAt;
      return {
        tag,
        key: tag.uuid ? String(tag.uuid) : tag.id != null ? `id-${tag.id}` : `row-${index}`,
        name: (tag.name ?? '').toString().trim() || 'Untitled tag',
        description: (tag.description ?? '').toString().trim() || null,
        created: formatWhen(tag.createdAt),
        createdTitle: stampTitle(tag.createdAt),
        updated: formatWhen(updated),
        updatedTitle: stampTitle(updated),
      };
    });
}

@Component({
  selector: 'tags-table',
  standalone: true,
  imports: [
    PromptCardComponent,
    PaginationComponent,
    EditTagModalComponent,
    EmptyStateComponent,
    DataTableComponent,
    HasPermissionDirective,
    MatTooltipModule,
  ],
  templateUrl: './tags-table.component.html',
  styleUrls: ['../group-table/group-table.css', './tags-table.component.css'],
})
export class TagsTableComponent implements OnInit, OnChanges {
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private messageService = inject(MessageService);
  private audience = inject(GroupAudienceService);
  contactsService = inject(ContactsService);

  /** Search and sort from the list page. A new object on each change. */
  @Input() params: Record<string, any> = {};
  @Input() searchTerm = '';
  @Output() getSelections: EventEmitter<ITag[]> = new EventEmitter();
  @Output() totalChange = new EventEmitter<number>();
  @Output() clearFilters = new EventEmitter<void>();

  readonly formatCount = formatCount;

  tags: ITag[] = [];
  rows: TagRow[] = [];
  counts: Record<string, CountState> = {};
  tagsPagination: IPagination = {
    totalElements: 0,
    pageSize: 10,
    totalPages: 1,
    pageNumber: 0,
    offSet: 0,
  };

  loading = true;
  loadError = '';
  pageIndex = 0;
  pageSize = 10;
  private loadSub?: Subscription;
  private started = false;

  readonly selection = new GroupSelection<ITag>();

  // reactive Data
  selectedTag: ITag | null = null;
  showDeletePrompt: boolean = false;
  showEditModal: boolean = false;
  deleting = false;

  // ── Loading ──
  load() {
    this.started = true;
    this.loadSub?.unsubscribe();
    this.loading = true;
    this.loadError = '';
    this.loadSub = this.audience
      .listTags({ ...this.params, page: this.pageIndex, size: this.pageSize })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (page) => {
          const pagination = page.itemPagination;
          const lastPage = Math.max(0, (pagination?.totalPages ?? 1) - 1);
          if (!page.items.length && this.pageIndex > lastPage && (pagination?.totalElements ?? 0) > 0) {
            this.pageIndex = lastPage;
            this.load();
            return;
          }
          this.tags = page.items;
          this.rows = toRows(page.items);
          this.tagsPagination = { ...pagination, pageNumber: this.pageIndex };
          this.selection.refresh(page.items);
          this.loading = false;
          this.totalChange.emit(pagination?.totalElements ?? page.items.length);
          this.loadCounts();
        },
        error: () => {
          this.loading = false;
          this.loadError = 'Tags did not load. Check your connection and try again.';
        },
      });
  }

  private loadCounts() {
    for (const row of this.rows) {
      const uuid = row.tag.uuid;
      if (!uuid || typeof this.counts[uuid] === 'number') continue;
      this.counts[uuid] = undefined;
      this.audience
        .memberCount({ kind: 'tag', uuid })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (count) => (this.counts = { ...this.counts, [uuid]: count }),
          error: () => (this.counts = { ...this.counts, [uuid]: null }),
        });
    }
  }

  countOf(tag: ITag | null): CountState {
    return tag?.uuid ? this.counts[tag.uuid] : null;
  }

  countState(tag: ITag): 'loading' | 'none' | 'known' {
    const count = this.countOf(tag);
    return count === undefined ? 'loading' : count === null ? 'none' : 'known';
  }

  // ── Paging ──
  goToPreviousPage() {
    this.updateTagPage(this.pageIndex - 1);
  }
  goToNextPage() {
    this.updateTagPage(this.pageIndex + 1);
  }
  updateTagPage(newPage: number) {
    this.pageIndex = Math.max(0, newPage);
    this.load();
  }
  /** The pager follows a size change with changePage(0), which loads. */
  onPageSizeChange(size: number) {
    this.pageSize = size;
  }

  // ── Selection ──
  selectTag(tag: ITag) {
    this.selection.toggle(tag);
    this.emitSelections();
  }

  deSelectAllTags() {
    this.selection.clear();
    this.emitSelections();
  }

  toggleSelectAllTags() {
    this.selection.togglePage(this.tags);
    this.emitSelections();
  }

  get getIsAllSelected() {
    return this.selection.allOn(this.tags);
  }

  get someOnPageSelected() {
    return this.selection.someOn(this.tags);
  }

  getTagIsSelected(tag: ITag) {
    return this.selection.has(tag);
  }

  emitSelections() {
    this.getSelections.emit(this.selection.items);
  }

  // ── Row actions ──
  updateSelectedTag(tag: ITag) {
    this.selectedTag = tag;
  }

  openDeletePrompt(tag: ITag) {
    this.updateSelectedTag(tag);
    this.showDeletePrompt = true;
  }
  closeDeletePrompt() {
    if (this.deleting) return;
    this.showDeletePrompt = false;
  }

  openEditModal(tag: ITag) {
    this.updateSelectedTag(tag);
    this.showEditModal = true;
  }
  closeEditModal() {
    this.showEditModal = false;
  }

  goToTagDetail(id: string) {
    id && this.router.navigate([`/organization/contacts/tags/${id}`]);
  }

  openFromKeyboard(event: Event, tag: ITag) {
    if (event.target !== event.currentTarget) return;
    event.preventDefault();
    this.goToTagDetail(tag.uuid || '');
  }

  /** Opens the composer with this tag already picked. */
  messageTag(tag: ITag) {
    this.messageService.updateSelectedTags([tag]);
    this.router.navigate(['/organization/messages/create']);
  }

  get deleteImpact(): string {
    const count = this.countOf(this.selectedTag);
    if (typeof count !== 'number') return 'Contacts with this tag stay in your contact list.';
    if (count === 0) return 'No contacts have this tag.';
    return `${countNoun(count, 'contact loses', 'contacts lose')} this tag. They stay in your contact list.`;
  }

  /** Deletes through the organisation-scoped batch endpoint with one uuid. */
  deleteTag() {
    const tag = this.selectedTag;
    if (!tag?.uuid || this.deleting) return;
    this.deleting = true;
    this.contactsService.deleteSelectedTags([tag.uuid]).subscribe({
      next: () => {
        this.deleting = false;
        this.selection.remove([tag]);
        this.emitSelections();
        this.showDeletePrompt = false;
        this.audience.invalidate({ kind: 'tag', uuid: tag.uuid! });
      },
      error: () => {
        this.deleting = false;
        this.showDeletePrompt = false;
        this.contactsService.showError(`${tag.name || 'The tag'} was not deleted. Try again.`);
      },
    });
  }

  get contactLoading() {
    return this.contactsService.isLoading;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['params']) {
      this.pageIndex = 0;
      this.load();
    }
  }

  ngOnInit() {
    if (!this.started) this.load();

    // ContactsService reloads its shared list after a create, edit or delete;
    // reload this page with its own search and sort.
    this.contactsService.getTags
      .pipe(skip(1), debounceTime(150), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.load());
  }
}
